import React from "react";
import Icon from "@accessdev/arc-ui-react/Icon";
import { useSelector, useDispatch } from "react-redux";
import { selectors } from "@Src/redux/reducers";
import { useToast } from "@accessdev/arc-ui-react/Toast";
import "./FavoriteThis.scss";
import { successMessage, failedMessage } from "@Src/constants/Favorites";
import { ApiStatus } from "@Src/constants";
import useAuthHooks from "@Src/components/Auth/useAuthHooks";
import actions from "@Src/redux/rootActions";

const { addFavorite, removeFavorite } = actions.favorites;

const useFavoriteStatus = (id) => {
  const userIsAuthenticated = useSelector(selectors.hasAuthenticatedUser);
  const favoriteList = useSelector(selectors.getFavoritesList);
  const getApiStatus = useSelector(selectors.getApiStatus);

  const uniqueFavoriteList = React.useMemo(() => {
    return favoriteList.reduce((acc, current) => {
      const exists = acc.find((item) => item.brand_id === current.brand_id);
      return exists ? acc : [...acc, current];
    }, []);
  }, [favoriteList]);

  const favoriteItem = uniqueFavoriteList.find((favorite) => favorite.brand_id === id);
  const favoriteId = favoriteItem ? favoriteItem.favorite_id : "";
  const brandId = favoriteItem ? favoriteItem.brand_id : "";

  return {
    userIsAuthenticated,
    getApiStatus,
    favoriteItem,
    favoriteId,
    brandId,
  };
};

const FavoriteThis = ({ id, name, className, size = "base" }) => {
  const { handleSigninClick } = useAuthHooks();
  const needsToFav = React.useRef(false);
  const [isFavorited, setIsFavorited] = React.useState(false);
  const { userIsAuthenticated, getApiStatus, favoriteItem, favoriteId, brandId } =
    useFavoriteStatus(id);
  const dispatch = useDispatch();
  const { addToast } = useToast();
  // Remove HTML tags from name
  const cleanName = name && name.replace(/<[^>]+>/g, "");
  const successText = isFavorited
    ? successMessage("remove", cleanName)
    : successMessage("add", cleanName);
  const failedText = isFavorited
    ? failedMessage("remove", cleanName)
    : failedMessage("add", cleanName);

  React.useEffect(() => {
    if (userIsAuthenticated) {
      setIsFavorited(!!favoriteItem);
    } else {
      setIsFavorited(false);
    }
  }, [favoriteItem, userIsAuthenticated]);

  const handleFavoriteAction = React.useCallback(async () => {
    const payload = { favoriteId: favoriteId, brandId: brandId };
    try {
      if (isFavorited) {
        await dispatch(removeFavorite(payload));
      } else {
        await dispatch(addFavorite(id));
      }
      if (getApiStatus.status === ApiStatus.SUCCESS) {
        addToast(successText);
        setIsFavorited(!isFavorited);
      } else if (getApiStatus.status === ApiStatus.FAILURE) {
        addToast(failedText);
      }
    } catch (error) {
      console.error(`Error occurred while ${isFavorited ? "removing" : "adding"} favorite:`, error);
    }
  }, [
    favoriteId,
    brandId,
    isFavorited,
    getApiStatus.status,
    dispatch,
    id,
    addToast,
    successText,
    failedText,
  ]);

  const doAddFavorite = React.useCallback(() => {
    if (needsToFav.current) {
      if (!favoriteItem) {
        needsToFav.current = false;
        setTimeout(handleFavoriteAction, 200);
      }
      needsToFav.current = false;
    }
  }, [favoriteItem, needsToFav, handleFavoriteAction]);

  React.useEffect(() => {
    if (userIsAuthenticated) {
      doAddFavorite();
    } else {
      needsToFav.current = false;
    }
  }, [userIsAuthenticated, doAddFavorite]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (!userIsAuthenticated) {
      needsToFav.current = true;
      handleSigninClick();
      return;
    }

    handleFavoriteAction();
  };

  return (
    <div className={className || "FavoriteThis"}>
      <Icon
        size={size}
        type="button"
        data-testid="favIcon"
        icon={isFavorited ? "favorite" : "favorite_border"}
        className={`favIcon ${isFavorited ? "active" : ""}`}
        onClick={handleToggle}
      />
    </div>
  );
};

export default FavoriteThis;
